import React, { useEffect, useMemo, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Input } from 'iebt-zing-storybook'
import ReactQuill from 'react-quill'
import 'react-quill/dist/quill.snow.css'
import { AxiosResponse } from 'axios'
import { useSmartNavigateLegacy as useNavigate } from 'components/SmartLink'
import { FileItem } from 'components/FileItem'
import { MeetingRoomSupportFileItem } from 'domain/feature/hub/company/meeting-room/manage-meeting-room/MeetingRoomForm/MeetingRoomSupportFileItem'
import { trans } from 'sharedKernel/i18n'
import i18n from 'sharedKernel/i18n/translate/config'
import { INPUT_MASK_FORMAT } from 'sharedKernel/constants'
import { formatDateForDisplay, getHubRoute, isDateBeforeToday } from 'sharedKernel/handlers'
import { ROUTES } from 'sharedKernel/constants'
import { showToast } from 'components/toast'
import { QuillEditorWrapper } from 'domain/feature/hub/company/challenge/program/styled'
import {
  useGetOpportunityStatus,
  useSaveOpportunity,
  ScoutingOpportunity,
} from 'domain/feature/hub/company/scouting/api/scoutingService'
import {
  getOpportunityFiles,
  getOpportunitySupportFileDisplayName,
  getOpportunitySupportFiles,
} from 'domain/feature/hub/company/scouting/scoutingOpportunityDisplay'
import {
  createDefaultVerticalSelection,
  OPPORTUNITY_VERTICAL_OPTIONS,
  OPPORTUNITY_VERTICAL_OTHER_ID,
} from './constants'
import {
  isOpportunityFormValid,
  validateOpportunityForm,
} from './buildPayload'
import {
  getVisibleExistingSupportFiles,
  hasVisibleExistingCover,
  markOpportunityFileForRemoval,
  syncOpportunityOnSave,
} from './opportunityFileSync'
import { OpportunityFormData } from './types'
import {
  CardSection,
  CheckboxGroup,
  CheckboxItem,
  DescriptionCharCount,
  FieldGroup,
  FieldLabel,
  FormHeader,
  FormTitle,
  HelperText,
  ImageNoteText,
  ImagePreview,
  ImagePreviewRemoveButton,
  ImagePreviewWrapper,
  ImagesUploadRow,
  OpportunityFormWrapper,
  RequiredMark,
  SaveButton,
  SectionLabel,
  SupportMaterialsList,
  UploadBtn,
} from './styles'

type CoverImagePreview = {
  file: File
  previewUrl: string
}

interface OpportunityFormProps {
  initialData?: Partial<OpportunityFormData>
  opportunityId?: string
  setIsNavigationBlocked: (blocked: boolean) => void
}

const DESCRIPTION_MAX_LENGTH = 5000

const formatToBRL = (digits: string): string => {
  if (!digits) return ''
  const cents = parseInt(digits, 10)
  if (isNaN(cents)) return ''
  return (cents / 100).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const parseInitialMaxValue = (raw: string): string => {
  if (!raw || !raw.trim()) return ''
  const cleaned = raw.replace(/[R$\s]/g, '').replace(/\./g, '').replace(',', '.')
  const num = parseFloat(cleaned)
  if (isNaN(num)) return raw
  return num.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

const descriptionEditorModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['link'],
    ['clean'],
  ],
}

const getDescriptionTextLength = (html: string): number => {
  const text = html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()
  return text.length
}

const defaultFormData = (): OpportunityFormData => ({
  offeringInstitutionName: '',
  interestDeadline: '',
  opportunityTitle: '',
  description: '',
  verticalSectors: createDefaultVerticalSelection(),
  verticalOther: '',
  maxValue: '',
  impactedBy: '',
  references: '',
  supportLink: '',
  supportMaterials: [],
  existingSupportFiles: [],
  removedFileIds: [],
  coverImage: null,
})

const getVerticalOptionLabel = (option: {
  id: string
  label?: string
  labelKey?: string
}): string => {
  if (option.labelKey) {
    return trans(option.labelKey)
  }
  return option.label ?? option.id
}

export const OpportunityForm = ({
  initialData,
  opportunityId,
  setIsNavigationBlocked,
}: OpportunityFormProps): JSX.Element => {
  const navigate = useNavigate()
  const supportMaterialsInputRef = useRef<HTMLInputElement>(null)
  const coverImageInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState<OpportunityFormData>({
    ...defaultFormData(),
    ...initialData,
    verticalSectors: initialData?.verticalSectors ?? createDefaultVerticalSelection(),
    verticalOther: initialData?.verticalOther ?? '',
    supportMaterials: initialData?.supportMaterials ?? [],
    existingSupportFiles: initialData?.existingSupportFiles ?? [],
    removedFileIds: initialData?.removedFileIds ?? [],
  })
  const [descriptionLength, setDescriptionLength] = useState(() =>
    getDescriptionTextLength(initialData?.description ?? '')
  )

  const { data: statusData } = useGetOpportunityStatus()
  const defaultStatusId = statusData?.rows?.[0]?.id

  const labels = useMemo(() => ({
    title: opportunityId
      ? trans('scouting.opportunity.editTitle')
      : trans('scouting.opportunity.createTitle'),
    saveButton: opportunityId
      ? trans('scouting.opportunity.saveChanges')
      : trans('scouting.opportunity.createButton'),
    offeringInstitutionName: trans('scouting.opportunity.field.offeringInstitutionName'),
    offeringInstitutionNamePlaceholder: trans('scouting.opportunity.field.offeringInstitutionNamePlaceholder'),
    interestDeadline: trans('scouting.opportunity.field.interestDeadline'),
    datePlaceholder: trans('scouting.opportunity.field.datePlaceholder'),
    opportunityTitle: trans('scouting.opportunity.field.opportunityTitle'),
    opportunityTitleHelper: trans('scouting.opportunity.field.opportunityTitleHelper'),
    opportunityTitlePlaceholder: trans('scouting.opportunity.field.opportunityTitlePlaceholder'),
    description: trans('scouting.opportunity.field.description'),
    descriptionHelper: trans('scouting.opportunity.field.descriptionHelper'),
    descriptionPlaceholder: trans('scouting.opportunity.field.descriptionPlaceholder'),
    descriptionCharacterCount: (current: number, max: number) =>
      String(i18n.t('scouting.opportunity.field.descriptionCharacterCount', { current, max })),
    verticalSector: trans('scouting.opportunity.field.verticalSector'),
    verticalOtherPlaceholder: trans('scouting.opportunity.field.verticalOtherPlaceholder'),
    maxValue: trans('scouting.opportunity.field.maxValue'),
    maxValuePlaceholder: trans('scouting.opportunity.field.maxValuePlaceholder'),
    impactedBy: trans('scouting.opportunity.field.impactedBy'),
    impactedByHelper: trans('scouting.opportunity.field.impactedByHelper'),
    impactedByPlaceholder: trans('scouting.opportunity.field.impactedByPlaceholder'),
    references: trans('scouting.opportunity.field.references'),
    referencesHelper: trans('scouting.opportunity.field.referencesHelper'),
    referencesPlaceholder: trans('scouting.opportunity.field.referencesPlaceholder'),
    supportLink: trans('scouting.opportunity.field.supportLink'),
    supportLinkHelper: trans('scouting.opportunity.field.supportLinkHelper'),
    supportLinkPlaceholder: trans('scouting.opportunity.field.supportLinkPlaceholder'),
    supportMaterials: trans('scouting.opportunity.field.supportMaterials'),
    addFile: trans('scouting.opportunity.field.addFile'),
    coverImage: trans('scouting.opportunity.field.coverImage'),
    coverImageHelper: trans('scouting.opportunity.field.coverImageHelper'),
    addImage: trans('scouting.opportunity.field.addImage'),
    removeImage: trans('scouting.opportunity.field.removeImage'),
  }), [opportunityId])

  const [coverImagePreview, setCoverImagePreview] = useState<CoverImagePreview | null>(() => {
    const file = initialData?.coverImage ?? null
    if (!file) return null
    return { file, previewUrl: URL.createObjectURL(file) }
  })
  const coverImagePreviewRef = useRef(coverImagePreview)
  coverImagePreviewRef.current = coverImagePreview

  useEffect(() => {
    return () => {
      const preview = coverImagePreviewRef.current
      if (preview?.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(preview.previewUrl)
      }
    }
  }, [])

  const hasAnyCoverImage = hasVisibleExistingCover(formData) || coverImagePreview != null
  const visibleExistingSupportFiles = getVisibleExistingSupportFiles(formData)
  const hasAnySupportMaterial =
    visibleExistingSupportFiles.length > 0 || formData.supportMaterials.length > 0

  const isFormValid = useMemo(() => isOpportunityFormValid(formData), [formData])

  const isDeadlinePast = useMemo(() => {
    const d = formData.interestDeadline
    return d.length === 10 && isDateBeforeToday(d)
  }, [formData.interestDeadline])

  const handleMaxValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void => {
    const digits = event.target.value.replace(/\D/g, '')
    updateField('maxValue', formatToBRL(digits))
  }

  const updateField = <K extends keyof OpportunityFormData>(
    key: K,
    value: OpportunityFormData[K]
  ): void => {
    setFormData(prev => ({ ...prev, [key]: value }))
    setIsNavigationBlocked(true)
  }

  const onSuccessSave = async (response: AxiosResponse): Promise<void> => {
    showToast('success', trans('scouting.save.success'))
    setIsNavigationBlocked(false)

    const savedId: string | undefined = response?.data?.id ?? opportunityId

    setTimeout(() => {
      if (savedId) {
        navigate(`/hub/company/scouting/${savedId}`)
      } else {
        navigate(getHubRoute(ROUTES.HUB.COMPANY.SCOUTING_LIST))
      }
    }, 1500)
  }

  const onErrorSave = async (): Promise<void> => {
    showToast('error', trans('scouting.save.error'))
    setIsNavigationBlocked(false)
  }

  const { mutateAsync: saveOpportunity } = useSaveOpportunity(undefined, onErrorSave)

  const handleSave = async (): Promise<void> => {
    const validation = validateOpportunityForm(formData)
    if (!validation.isValid) {
      showToast('error', validation.message)
      return
    }

    try {
      const response = await syncOpportunityOnSave(formData, saveOpportunity, {
        defaultStatusId,
        opportunityId,
      })

      await onSuccessSave(response as AxiosResponse)
    } catch {
      showToast('error', trans('scouting.save.error'))
    }
  }

  const handleSupportMaterialsChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (!file) return
    updateField('supportMaterials', [...formData.supportMaterials, file])
    event.target.value = ''
  }

  const markExistingCoverForRemoval = (): void => {
    setFormData(prev => ({
      ...prev,
      removedFileIds: markOpportunityFileForRemoval(
        prev.removedFileIds ?? [],
        prev.existingCoverImageId
      ),
      existingCoverImageId: undefined,
      existingCoverImagePath: undefined,
      existingCoverImageName: undefined,
    }))
    setIsNavigationBlocked(true)
  }

  const handleCoverImageChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const file = event.target.files?.[0]
    if (!file) return

    markExistingCoverForRemoval()

    setCoverImagePreview(prev => {
      if (prev?.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(prev.previewUrl)
      }
      return { file, previewUrl: URL.createObjectURL(file) }
    })
    updateField('coverImage', file)
    event.target.value = ''
  }

  const handleRemoveCoverImage = (): void => {
    markExistingCoverForRemoval()

    setCoverImagePreview(prev => {
      if (prev?.previewUrl.startsWith('blob:')) {
        URL.revokeObjectURL(prev.previewUrl)
      }
      return null
    })
    updateField('coverImage', null)
  }

  const removeExistingSupportFile = (fileId: string): void => {
    setFormData(prev => ({
      ...prev,
      removedFileIds: markOpportunityFileForRemoval(prev.removedFileIds ?? [], fileId),
    }))
    setIsNavigationBlocked(true)
  }

  const removeSupportMaterial = (index: number): void => {
    updateField(
      'supportMaterials',
      formData.supportMaterials.filter((_, fileIndex) => fileIndex !== index)
    )
  }

  const showVerticalOther = formData.verticalSectors[OPPORTUNITY_VERTICAL_OTHER_ID]

  return (
    <OpportunityFormWrapper>
      <FormHeader>
        <FormTitle>{labels.title}</FormTitle>
        <SaveButton
          type="button"
          $disabled={!isFormValid}
          disabled={!isFormValid}
          onClick={handleSave}
        >
          {labels.saveButton}
        </SaveButton>
      </FormHeader>

      <CardSection>
        <FieldGroup>
          <FieldLabel htmlFor="offeringInstitutionName">
            {labels.offeringInstitutionName}<RequiredMark>*</RequiredMark>
          </FieldLabel>
          <Input
            id="offeringInstitutionName"
            placeholder={labels.offeringInstitutionNamePlaceholder}
            value={formData.offeringInstitutionName}
            showCounter={false}
            onChange={(event) => updateField('offeringInstitutionName', event.target.value)}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="interestDeadline">
            {labels.interestDeadline}<RequiredMark>*</RequiredMark>
          </FieldLabel>
          <Input
            id="interestDeadline"
            placeholder={labels.datePlaceholder}
            value={formData.interestDeadline}
            code="masked"
            mask={INPUT_MASK_FORMAT.DATE}
            showCounter={false}
            isInvalid={isDeadlinePast}
            notValidFieldMessage={trans('scouting.opportunity.validation.interestDeadlineBeforeToday')}
            onChange={(event) => updateField('interestDeadline', event.target.value)}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="opportunityTitle">
            {labels.opportunityTitle}<RequiredMark>*</RequiredMark>
          </FieldLabel>
          <HelperText>{labels.opportunityTitleHelper}</HelperText>
          <Input
            id="opportunityTitle"
            placeholder={labels.opportunityTitlePlaceholder}
            value={formData.opportunityTitle}
            showCounter={false}
            onChange={(event) => updateField('opportunityTitle', event.target.value)}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="description">
            {labels.description}<RequiredMark>*</RequiredMark>
          </FieldLabel>
          <HelperText>{labels.descriptionHelper}</HelperText>
          <QuillEditorWrapper>
            <ReactQuill
              theme="snow"
              value={formData.description}
              onChange={(content, _delta, _source, editor) => {
                updateField('description', content)
                setDescriptionLength(Math.max(0, editor.getText().length - 1))
              }}
              modules={descriptionEditorModules}
              placeholder={labels.descriptionPlaceholder}
            />
          </QuillEditorWrapper>
          <DescriptionCharCount>
            {labels.descriptionCharacterCount(descriptionLength, DESCRIPTION_MAX_LENGTH)}
          </DescriptionCharCount>
        </FieldGroup>

        <FieldGroup>
          <FieldLabel>
            {labels.verticalSector}<RequiredMark>*</RequiredMark>
          </FieldLabel>
          <CheckboxGroup>
            {OPPORTUNITY_VERTICAL_OPTIONS.map(option => (
              <CheckboxItem
                key={option.id}
                type="checkbox"
                id={`vertical-${option.id}`}
                label={getVerticalOptionLabel(option)}
                checked={formData.verticalSectors[option.id]}
                onChange={(event) =>
                  updateField('verticalSectors', {
                    ...formData.verticalSectors,
                    [option.id]: event.target.checked,
                  })
                }
              />
            ))}
          </CheckboxGroup>
          {showVerticalOther && (
            <div style={{ marginTop: 12 }}>
              <Input
                id="verticalOther"
                placeholder={labels.verticalOtherPlaceholder}
                value={formData.verticalOther}
                showCounter={false}
                onChange={(event) => updateField('verticalOther', event.target.value)}
              />
            </div>
          )}
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="maxValue">{labels.maxValue}</FieldLabel>
          <Input
            id="maxValue"
            placeholder={labels.maxValuePlaceholder}
            value={formData.maxValue}
            showCounter={false}
            onChange={handleMaxValueChange}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="impactedBy">
            {labels.impactedBy}<RequiredMark>*</RequiredMark>
          </FieldLabel>
          <HelperText>{labels.impactedByHelper}</HelperText>
          <Input
            id="impactedBy"
            placeholder={labels.impactedByPlaceholder}
            value={formData.impactedBy}
            showCounter={false}
            onChange={(event) => updateField('impactedBy', event.target.value)}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="references">{labels.references}</FieldLabel>
          <HelperText>{labels.referencesHelper}</HelperText>
          <Input
            id="references"
            placeholder={labels.referencesPlaceholder}
            value={formData.references}
            showCounter={false}
            onChange={(event) => updateField('references', event.target.value)}
          />
        </FieldGroup>

        <FieldGroup>
          <FieldLabel htmlFor="supportLink">{labels.supportLink}</FieldLabel>
          <HelperText>{labels.supportLinkHelper}</HelperText>
          <Input
            id="supportLink"
            placeholder={labels.supportLinkPlaceholder}
            value={formData.supportLink}
            showCounter={false}
            onChange={(event) => updateField('supportLink', event.target.value)}
            onBlur={() => {
              const value = formData.supportLink.trim()
              if (value && !/^https?:\/\//i.test(value)) {
                updateField('supportLink', `https://${value}`)
              }
            }}
          />
        </FieldGroup>

        <FieldGroup>
          <SectionLabel>
            {labels.coverImage}<RequiredMark>*</RequiredMark>
          </SectionLabel>
          <ImageNoteText>{labels.coverImageHelper}</ImageNoteText>
          <ImagesUploadRow>
            {hasVisibleExistingCover(formData) && !coverImagePreview && (
              <ImagePreviewWrapper>
                <ImagePreview
                  src={formData.existingCoverImagePath}
                  alt={formData.existingCoverImageName ?? labels.coverImage}
                />
                <ImagePreviewRemoveButton
                  type="button"
                  onClick={handleRemoveCoverImage}
                  title={labels.removeImage}
                  aria-label={labels.removeImage}
                >
                  ×
                </ImagePreviewRemoveButton>
              </ImagePreviewWrapper>
            )}
            {coverImagePreview && (
              <ImagePreviewWrapper>
                <ImagePreview
                  src={coverImagePreview.previewUrl}
                  alt={coverImagePreview.file.name}
                />
                <ImagePreviewRemoveButton
                  type="button"
                  onClick={handleRemoveCoverImage}
                  title={labels.removeImage}
                  aria-label={labels.removeImage}
                >
                  ×
                </ImagePreviewRemoveButton>
              </ImagePreviewWrapper>
            )}
            <input
              ref={coverImageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleCoverImageChange}
            />
            {!hasAnyCoverImage && (
              <UploadBtn type="button" onClick={() => coverImageInputRef.current?.click()}>
                <FontAwesomeIcon icon={['fas', 'cloud-arrow-up']} style={{ marginRight: '6px' }} />
                {labels.addImage}
              </UploadBtn>
            )}
          </ImagesUploadRow>
        </FieldGroup>

        <FieldGroup>
          <SectionLabel>{labels.supportMaterials}</SectionLabel>

          {hasAnySupportMaterial && (
            <SupportMaterialsList>
              {visibleExistingSupportFiles.map(file => (
                <MeetingRoomSupportFileItem
                  key={file.id}
                  fileName={file.name}
                  fileUrl={file.path}
                  mimeType={file.mimetype}
                  onRemove={() => removeExistingSupportFile(file.id)}
                />
              ))}
              {formData.supportMaterials.map((file, index) => (
                <FileItem
                  key={`${file.name}-${index}`}
                  file={file}
                  onRemove={() => removeSupportMaterial(index)}
                />
              ))}
            </SupportMaterialsList>
          )}

          <input
            ref={supportMaterialsInputRef}
            type="file"
            accept=".pdf,.doc,.docx"
            style={{ display: 'none' }}
            onChange={handleSupportMaterialsChange}
          />
          <UploadBtn type="button" onClick={() => supportMaterialsInputRef.current?.click()}>
            <FontAwesomeIcon icon={['fas', 'cloud-arrow-up']} style={{ marginRight: '6px' }} />
            {labels.addFile}
          </UploadBtn>
        </FieldGroup>
      </CardSection>
    </OpportunityFormWrapper>
  )
}

export const mapScoutingOpportunityToForm = (
  opportunity: ScoutingOpportunity
): Partial<OpportunityFormData> => {
  const extended = opportunity as ScoutingOpportunity & {
    institutionName?: string
    impactedAudience?: string
    maximumOpportunityValue?: number | string
  }

  const formatMaxValue = (): string => {
    const raw =
      extended.maximumOpportunityValue != null && extended.maximumOpportunityValue !== ''
        ? String(extended.maximumOpportunityValue)
        : (opportunity.maxValue ?? '')
    return parseInitialMaxValue(raw)
  }

  const coverFile = getOpportunityFiles(opportunity).find(
    file => (file.type ?? '').toLowerCase() === 'coverimage'
  )
  const supportFiles = getOpportunitySupportFiles(undefined, opportunity)

  return {
    offeringInstitutionName: opportunity.requesterName ?? extended.institutionName ?? '',
    opportunityTitle: opportunity.title ?? '',
    description: opportunity.description ?? '',
    interestDeadline: formatDateForDisplay(opportunity.interestDeadline),
    maxValue: formatMaxValue(),
    impactedBy: extended.impactedAudience ?? opportunity.impactedBy ?? '',
    references: opportunity.references ?? '',
    supportLink: opportunity.supportLink ?? '',
    existingCoverImagePath: coverFile?.path,
    existingCoverImageName: coverFile?.name,
    existingCoverImageId: coverFile?.id,
    existingSupportFiles: supportFiles
      .filter(file => file.id && file.path)
      .map(file => ({
        id: file.id as string,
        path: file.path as string,
        name: getOpportunitySupportFileDisplayName(file, opportunity.title),
        extension: file.extension,
        mimetype: file.mimetype,
      })),
    removedFileIds: [],
    ...(() => {
      const verticalSectors = createDefaultVerticalSelection()
      let verticalOther = opportunity.otherVertical ?? ''

      if (opportunity.verticals?.length) {
        opportunity.verticals.forEach(vertical => {
          const normalized = vertical.toLowerCase()
          if (normalized === 'other') {
            verticalSectors[OPPORTUNITY_VERTICAL_OTHER_ID] = true
            return
          }

          const matchedOption = OPPORTUNITY_VERTICAL_OPTIONS.find(option => option.id === vertical)
          if (matchedOption) {
            verticalSectors[matchedOption.id] = true
          }
        })
      } else {
        const operationArea = opportunity.operationArea ?? ''

        operationArea.split(',').map(part => part.trim()).filter(Boolean).forEach(part => {
          const matchedOption = OPPORTUNITY_VERTICAL_OPTIONS.find(
            option => getVerticalOptionLabel(option).toLowerCase() === part.toLowerCase()
          )
          if (matchedOption) {
            verticalSectors[matchedOption.id] = true
          } else {
            verticalSectors[OPPORTUNITY_VERTICAL_OTHER_ID] = true
            verticalOther = part
          }
        })
      }

      if (verticalSectors[OPPORTUNITY_VERTICAL_OTHER_ID]) {
        verticalOther = opportunity.otherVertical ?? verticalOther
      }

      return { verticalSectors, verticalOther }
    })(),
  }
}
